import { useCallback, useEffect, useState } from 'react'

const STORAGE_KEY = 'kilele:watchlist'
const CHANGE_EVENT = 'kilele-watchlist-change'

const readWatchlist = (): string[] => {
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY)
        const parsed: unknown = raw ? JSON.parse(raw) : []

        return Array.isArray(parsed) ? parsed.filter((item): item is string => typeof item === 'string') : []
    }
    catch {
        return []
    }
}

export interface Watchlist {
    items: string[]
    ready: boolean
    has: (id: string) => boolean
    toggle: (id: string) => void
}

export const useWatchlist = (): Watchlist => {
    const [items, setItems] = useState<string[]>([])
    const [ready, setReady] = useState(false)

    useEffect(() => {
        setItems(readWatchlist())
        setReady(true)

        const sync = () => setItems(readWatchlist())

        window.addEventListener(CHANGE_EVENT, sync)
        window.addEventListener('storage', sync)

        return () => {
            window.removeEventListener(CHANGE_EVENT, sync)
            window.removeEventListener('storage', sync)
        }
    }, [])

    const toggle = useCallback((id: string) => {
        const current = readWatchlist()
        const next = current.includes(id) ? current.filter(item => item !== id) : [...current, id]

        try {
            window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
        }
        catch {
            // Storage can be unavailable in private browsing; the in-memory list still updates.
        }

        setItems(next)
        window.dispatchEvent(new Event(CHANGE_EVENT))
    }, [])

    const has = useCallback((id: string) => items.includes(id), [items])

    return { items, ready, has, toggle }
}
